import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Robust geolocation hook.
 *
 * Returns:
 *   { state, coords, error, request, lastUpdated }
 *
 * state values:
 *   "idle"        — nothing requested yet
 *   "requesting"  — permission prompt / position fetch in flight
 *   "granted"     — permission granted; `coords` is fresh
 *   "denied"      — user blocked access (show retry + instructions)
 *   "unavailable" — browser doesn't support geolocation
 *   "error"       — timeout / position unavailable / other
 *
 * Designed to be used inside <LocationGate /> or any screen that needs GPS.
 */
export function useGeolocation({ auto = true, watch = false } = {}) {
  const [state, setState] = useState("idle");
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const watchIdRef = useRef(null);

  const request = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState("unavailable");
      setError("Geolocation is not supported by this browser.");
      return;
    }
    setState("requesting");
    setError(null);

    const onSuccess = (p) => {
      setCoords({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
      setLastUpdated(Date.now());
      setState("granted");
      setError(null);
    };
    const onError = (err) => {
      // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
      if (err.code === 1) {
        setState("denied");
        setError("Location permission denied.");
      } else if (err.code === 2) {
        setState("error");
        setError("Couldn't get your location. Make sure GPS is on.");
      } else if (err.code === 3) {
        setState("error");
        setError("Location request timed out. Try again.");
      } else {
        setState("error");
        setError(err.message || "Unknown location error.");
      }
    };

    const opts = { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 };

    if (watch) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
      watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, opts);
    } else {
      navigator.geolocation.getCurrentPosition(onSuccess, onError, opts);
    }
  }, [watch]);

  useEffect(() => {
    if (auto) request();
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, coords, error, request, lastUpdated };
}
